/**
 * FASE 24.5 — SOFT RECOMMENDATION ENGINE
 *
 * Generates extremely gentle pastoral suggestions.
 * Never insistent. Never mechanical. Never urgent without cause.
 *
 * Rules:
 *  - Max 1 recommendation at a time
 *  - Per-type cooldown (same recommendation not repeated for 10 min)
 *  - Emotional tone awareness (grief context → no recommendations)
 *  - Never suggests the same thing twice in a session for the same entity
 */

import { getPriorityWorkflows } from "./workflowPriorities";
import { computeFollowupItems }  from "./followupEngine";
import { getWorkflowDefinition } from "./workflowSchemas";
import { softSuggestionOpener }  from "../kevia-core/pastoralToneGuide";

/* ─── Session-level cooldown ─────────────────────────────────── */

/** @type {Map<string, number>} typeKey → last-shown timestamp */
const _shown         = new Map();
const COOLDOWN_MS    = 10 * 60 * 1000; // 10 min
const SESSION_BLOCK  = new Set();        // entity:type pairs shown this session

/**
 * @typedef {Object} SoftRecommendation
 * @property {string}      text
 * @property {number}      confidence — 0-1
 * @property {string|null} workflowId
 * @property {string|null} entityName
 * @property {string}      type — recommendation category
 */

function isCooled(key) {
  const last = _shown.get(key);
  return last === undefined || Date.now() - last >= COOLDOWN_MS;
}

function markShown(key) {
  _shown.set(key, Date.now());
  SESSION_BLOCK.add(key);
}

/**
 * Get a soft recommendation based on current workflow state.
 *
 * @param {{
 *   entityContext?: object,
 *   mood?: string,       — from resolveKeviaMood (grief suppresses)
 *   appScope?: object,
 * }} opts
 * @returns {SoftRecommendation|null}
 */
export function getSoftRecommendation({ entityContext = null, mood = "neutral", appScope = null } = {}) {
  // Grief context → never surface recommendations
  if (mood === "grief") return null;

  const followups = computeFollowupItems();
  const topWfs    = getPriorityWorkflows(3);

  /* ── Priority 1: overdue followup for current entity ─────── */
  if (entityContext?.entityId) {
    const entityFollowup = followups.find(
      (i) => i.workflowId && i.kind === "overdue" &&
      topWfs.some((w) => w.id === i.workflowId && w.related_entities.some((e) => e.id === entityContext.entityId)),
    );
    if (entityFollowup) {
      const key = `overdue:${entityFollowup.workflowId}`;
      if (isCooled(key) && !SESSION_BLOCK.has(key)) {
        const opener = softSuggestionOpener(0);
        const def    = getWorkflowDefinition(topWfs.find((w) => w.id === entityFollowup.workflowId)?.type ?? "");
        markShown(key);
        return {
          text:       `${opener} retomar ${def?.label ?? "el seguimiento"} con ${entityContext.name ?? "esta persona"}.`,
          confidence: 0.80,
          workflowId: entityFollowup.workflowId,
          entityName: entityContext.name ?? null,
          type:       "overdue_followup",
        };
      }
    }
  }

  /* ── Priority 2: top overdue item globally ──────────────── */
  const topOverdue = followups.find((i) => i.kind === "overdue");
  if (topOverdue) {
    const key = `overdue:${topOverdue.workflowId}`;
    if (isCooled(key) && !SESSION_BLOCK.has(key)) {
      const wf      = topWfs.find((w) => w.id === topOverdue.workflowId);
      const def     = wf ? getWorkflowDefinition(wf.type) : null;
      const opener  = softSuggestionOpener(1);
      markShown(key);
      return {
        text:       `${opener} darle una mirada a ${topOverdue.entityName ? `el proceso con ${topOverdue.entityName}` : def?.label ?? "un proceso pendiente"}.`,
        confidence: 0.70,
        workflowId: topOverdue.workflowId,
        entityName: topOverdue.entityName,
        type:       "overdue_global",
      };
    }
  }

  /* ── Priority 3: top active workflow's next step ──────────── */
  const topWf = topWfs[0];
  if (topWf) {
    const key = `step:${topWf.id}`;
    if (isCooled(key) && !SESSION_BLOCK.has(key)) {
      const def        = getWorkflowDefinition(topWf.type);
      const entityName = topWf.related_entities?.[0]?.name;
      const opener     = softSuggestionOpener(2);
      markShown(key);
      return {
        text:       `${opener} avanzar en ${entityName ? `${def?.label ?? topWf.type} con ${entityName}` : def?.label ?? topWf.type}.`,
        confidence: 0.60,
        workflowId: topWf.id,
        entityName,
        type:       "next_step",
      };
    }
  }

  return null;
}

/** Reset cooldown state (call on sign-out or new session). */
export function resetRecommendations() {
  _shown.clear();
  SESSION_BLOCK.clear();
}
